import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { changeLanguage, type AppLanguage } from '@/i18n';
import { cn } from '@/lib/utils';

/** The language each UI language switches to (one toggle per language pair). */
const OTHER_LANGUAGE: Readonly<Record<AppLanguage, AppLanguage>> = { en: 'ru', ru: 'en' };

interface LangSwitchProps {
  /** Extra positioning classes for the placement context (landing header, room top bar). */
  readonly className?: string;
}

/**
 * The compact language toggle: a small button that shows the code of the language it switches
 * TO (`RU` while in English, `EN` while in Russian) and carries its accessible name in the
 * language currently on screen. Clicking switches the UI, persists the choice under the
 * `vc.lang` localStorage key and updates <html lang>.
 */
export function LangSwitch({ className }: LangSwitchProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const current: AppLanguage = i18n.language === 'ru' ? 'ru' : 'en';
  const next = OTHER_LANGUAGE[current];
  const label = t('lang.switch_to', { name: t(`lang.name_${next}`) });
  return (
    <button
      type="button"
      data-testid="lang-switch"
      aria-label={label}
      title={label}
      onClick={() => {
        void changeLanguage(next);
      }}
      className={cn(
        'shrink-0 rounded-md border border-white/10 bg-zinc-800/60 px-2 py-1 font-mono text-[11px] font-semibold uppercase tracking-wide text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-white',
        className,
      )}
    >
      {t(`lang.code_${next}`)}
    </button>
  );
}
